import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import { sectionCardSx } from "views/system-design/sectionCardSx";
import DSButton from "views/system-design/DSButton";
import DSTypography from "views/system-design/DSTypography";
import { ContentProfile } from "../../../api/admin";
import { extractApiErrorMessage } from "../../../utils/util";
import { useUpdateContentProfile } from "../hooks/useContentProfiles";
import { ProfileDraft } from "../hooks/useProfileDraft";
import ShowKlydosSettings from "./settings/ShowKlydosSettings";
import SoundSettings from "./settings/SoundSettings";
import DisplaySettings from "./settings/DisplaySettings";
import ExtraSettings from "./settings/ExtraSettings";
import ProfileDefaultChimeCard from "./ProfileDefaultChimeCard";
import ManagedSettingsCard from "./ManagedSettingsCard";
import ProfileCollectionsCard from "./ProfileCollectionsCard";
import ProfileFavoritesCard from "./ProfileFavoritesCard";
import ProfileGiftCard from "./ProfileGiftCard";
import LockedSection from "./LockedSection";

interface SettingsTabProps {
  profile: ContentProfile;
  draft: ProfileDraft;
  locked: boolean;
}

/**
 * Saves explicitly, unlike DeviceModal, which writes every change straight
 * to Firestore — editing a profile must never touch a device by itself,
 * only Sync does (see the ticket's core invariant). The draft itself
 * (values/collections/dirty) is owned by ContentProfileDetail via
 * useProfileDraft, not this component, so it survives switching to the
 * Devices tab and back.
 */
const SettingsTab = ({ profile, draft, locked }: SettingsTabProps) => {
  const [error, setError] = useState<string | null>(null);
  const { mutate: save, isPending: isSaving } = useUpdateContentProfile();

  const handleSave = () => {
    setError(null);
    save(
      {
        id: profile.id,
        data: {
          values: draft.values,
          machineCollections: draft.machineCollections,
          favorites: draft.favorites,
        },
      },
      {
        onSuccess: () => draft.markSaved(),
        onError: (e) => setError(extractApiErrorMessage(e, "Failed to save")),
      },
    );
  };

  return (
    <DSBox>
      {!locked && (
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          {error && (
            <DSTypography variant="caption" color="error">
              {error}
            </DSTypography>
          )}
          <DSButton
            variant="contained"
            size="small"
            disabled={!draft.dirty || isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </DSButton>
        </DSBox>
      )}
      <LockedSection locked={locked}>
        {/* An order's gift comes from the Shopify order itself. */}
        {!profile.orderName && <ProfileGiftCard profile={profile} />}
        <ProfileFavoritesCard
          favorites={draft.favorites}
          onChange={draft.setFavorites}
        />
        <ProfileCollectionsCard
          title="Collections"
          collections={draft.machineCollections}
          onChange={draft.setMachineCollections}
        />

        <ManagedSettingsCard
          values={draft.values}
          onUnset={locked ? undefined : draft.unsetField}
        />

        <DSCard sx={[sectionCardSx, { mb: 2 }]}>
          <DSSectionHeading>Show Klydos</DSSectionHeading>
          <DSCardContent>
            <ShowKlydosSettings
              values={draft.values}
              onChange={draft.setField}
            />
          </DSCardContent>
        </DSCard>

        <DSCard sx={[sectionCardSx, { mb: 2 }]}>
          <DSSectionHeading>Sound</DSSectionHeading>
          <DSCardContent>
            <SoundSettings values={draft.values} onChange={draft.setField} />
          </DSCardContent>
        </DSCard>

        <DSCard sx={[sectionCardSx, { mb: 2 }]}>
          <DSSectionHeading>Display</DSSectionHeading>
          <DSCardContent>
            <DisplaySettings values={draft.values} onChange={draft.setField} />
          </DSCardContent>
        </DSCard>

        <ProfileDefaultChimeCard
          values={draft.values}
          collections={draft.machineCollections}
          onChange={draft.setField}
        />

        <DSCard sx={[sectionCardSx, { mb: 2 }]}>
          <DSSectionHeading>More</DSSectionHeading>
          <DSCardContent>
            <ExtraSettings values={draft.values} onChange={draft.setField} />
          </DSCardContent>
        </DSCard>
      </LockedSection>
    </DSBox>
  );
};

export default SettingsTab;
